import React from "react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { formatHours } from "../../../utils/timesheetUtils";

const EmployeeTimesheetTable = ({
  rows,
  loading,
  selectedUserId,
  onViewEmployee,
  page = 1,
  pageCount = 1,
  totalRows = rows.length,
  onPageChange = () => {},
}) => {
  if (loading) {
    return (
      <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-14 text-center shadow-sm">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          กำลังโหลดสรุปข้อมูล Dashboard...
        </p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-3xl border border-dashed border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-gray-800/40 px-6 py-14 text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          ยังไม่มีข้อมูลเวลาทำงานในช่วงนี้
        </h3>
      </div>
    );
  }

  return (
    <>
      <div className="hidden lg:block overflow-hidden rounded-3xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/60">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">
                  พนักงาน
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">
                  แผนก
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">
                  Team
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">
                  ชั่วโมงรวม
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">
                  การดำเนินการ
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const active = row.userId === selectedUserId;
                return (
                  <tr
                    key={row.userId}
                    className={[
                      "border-t border-gray-100 dark:border-gray-700/80",
                      active
                        ? "bg-indigo-100/70 dark:bg-indigo-500/20"
                        : rowIndex % 2 === 0
                        ? "bg-slate-50/80 dark:bg-slate-900/35"
                        : "bg-white dark:bg-gray-800",
                    ].join(" ")}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {row.username}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {row.department || "-"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {row.team || "-"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-200">
                      {formatHours(row.totalHours)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => onViewEmployee(row.userId)}
                          className="inline-flex items-center gap-2 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-500/10 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition"
                        >
                          ดู Timesheet
                          <FiArrowRight size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:hidden">
        {rows.map((row) => (
          <div
            key={row.userId}
            className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-all">
                  {row.username}
                </h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {row.department || "-"} • {row.team || "-"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500">
                  ชั่วโมง
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatHours(row.totalHours)}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onViewEmployee(row.userId)}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-500/10 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition"
            >
              ดู Timesheet
              <FiArrowRight size={16} />
            </button>
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            แสดง {rows.length} จาก {totalRows} รายการ
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:border-indigo-700"
            >
              <FiArrowLeft />
              ก่อนหน้า
            </button>
            <span className="min-w-20 text-center text-sm font-medium text-gray-700 dark:text-gray-200">
              หน้า {page} / {pageCount}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pageCount}
              className="inline-flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-indigo-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:border-indigo-700"
            >
              ถัดไป
              <FiArrowRight />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeTimesheetTable;
